import type { SupportedLanguage } from "../../../interfaces";
import { memoryStore } from "../../../lib/store";

/**
 * The language settled on for the current room, WITH its provenance.
 *
 * `source` is the whole point. A settled language arrives two ways that look
 * identical as a bare value and are not interchangeable:
 *
 *  - `"explicit"` — the reader chose it: in this session via the in-room picker, in a
 *    previous one via the persisted per-room override, or by an explicit `?lang=`
 *    deep link.
 *  - `"resolved"` — nobody chose it. `resolveRoomLanguage` fell through to
 *    `allowedLanguages[0]` because the reader's own language is not published by this
 *    room. That is order-derived semantics from a user-editable multi-select in the
 *    admin: not a decision anyone made, just whichever tag was added first.
 *
 * Room TEXT honours only `"explicit"`. A `"resolved"` default leaves the room's name
 * and description on the reader's interface locale, where the English fallback still
 * applies — English is medicine's lingua franca, and only 8 of 46 production rooms
 * have any description at all, so following an arbitrary published language would
 * frequently trade a readable English description for nothing.
 *
 * Content QUERIES use `language` regardless: an arbitrary published language is a
 * fine corpus to request, it is just not a reason to relabel a room.
 *
 * The distinction is recorded at each write site, where it is known. It cannot be
 * recovered downstream — by the time a bare language reaches a consumer, "chosen" and
 * "defaulted" are the same string.
 */
export type RoomContentLangSelection = {
  language: SupportedLanguage;
  source: "explicit" | "resolved";
};

/**
 * Holds `null` outside rooms, or when content follows the UI language — consumers
 * then fall back per their own rules (see `useActiveLang`).
 *
 * Reactive: written by `LangSync` (room resolution, `?lang=`) and the in-room
 * language picker; read reactively through `useActiveLang`.
 */
export const roomContentLangStore =
  memoryStore<RoomContentLangSelection | null>(null);

export function setRoomContentLang(
  selection: RoomContentLangSelection | null
): void {
  roomContentLangStore.set(selection);
}
